import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from '@environments';
import { EtapasCierre } from '@shared/enums/EtapasCierreTurno';
import { CargaDatos } from '@shared/models/cierreTurno/CargaDatos';
import { CierreTurno } from '@shared/models/cierreTurno/CierreTurno';
import { Comparativas } from '@shared/models/cierreTurno/Comparativas';
import { ParteDiario } from '@shared/models/cierreTurno/ParteDiario';
import { Step } from '@shared/models/cierreTurno/Step';
import { Responsable } from '@shared/models/stock/Responsable';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CierreDiarioService {

  cierreTurnoBase = environment.apiServer.cierreTurnoBaseUrl;
  stockBase = environment.apiServer.stockBaseUrl;
  facturacionBase = environment.apiServer.facturacionBaseUrl;
  private cierreURL = this.cierreTurnoBase + '/CierresTurnosV1/';

  private steps: Step[] = [
    { label: 'Etapa 1: Realizar cierre', completed: false, seleccionado: true },
    { label: 'Etapa 2: Carga de datos', completed: false, seleccionado: false },
    { label: 'Etapa 3: Cuadros de comparativas', completed: false, seleccionado: false },
    { label: 'Etapa 4: Parte diario', completed: false, seleccionado: false },
    { label: 'Finalizar Turno', completed: false, seleccionado: false }
  ];

  stepActual: Step;
  parteDiario: ParteDiario;
  cargaDatos: CargaDatos;
  primerIngreso: boolean = true;
  usuarioRealizoCambios: boolean = false;
  hayCierre: boolean;

  private stepsSubject = new BehaviorSubject<Step[]>(this.steps);
  steps$ = this.stepsSubject.asObservable();

  private cierreSubject = new BehaviorSubject<CierreTurno>(new CierreTurno())
  public cierre$ = this.cierreSubject.asObservable();

  private indexEtapaSubject = new BehaviorSubject<number>(0);
  public index$ = this.indexEtapaSubject.asObservable();

  loading = signal<boolean>(false);

  constructor(private http: HttpClient) { }

  resetSteps(index) {
    this.steps = this.steps.map((item, i) => ({
      ...item,
      completed: i <= this.cierreSubject.getValue()?.etapaActual
    }));
    this.steps.forEach(s => {
      s.seleccionado = false;
    });
    this.steps[index].seleccionado = true;
  }

  updateStepProgress(index: EtapasCierre, cierre: CierreTurno): void {
    this.cierreSubject.next(cierre);
    this.changeStepProgress(index + 1);
  }

  changeStepProgress(index: number): void {
    this.indexEtapaSubject.next(index);
    this.resetSteps(index);
    this.stepsSubject.next(this.steps);
    this.primerIngreso = false;
  }

  initStepProgress() {
    this.steps.forEach(s => s.completed = false);
  }

  get ingresoPrimeraVez(): boolean {
    return this.primerIngreso;
  }

  restart() {
    if (this.hayCierre) {
      this.changeStepProgress(EtapasCierre.RealizarCierre)
      this.primerIngreso = true;
      this.usuarioRealizoCambios = false;
      this.stepsSubject.next(this.steps);
      this.cierreSubject.next(null);
      this.indexEtapaSubject.next(0);
      this.hayCierre = false;
    }
  }

  setCierre(cierre: CierreTurno) {
    this.cierreSubject.next(cierre);
    this.hayCierre = true;
  }

  cargandoCierre() {
    this.loading.set(true);
  }

  cargoCierre() {
    this.loading.set(false);
  }

  getCierreTurno(codigoAeroplanta: string): Observable<CierreTurno> {
    return this.http.get<CierreTurno>(this.cierreURL + codigoAeroplanta + '/Cerrado');
  }

  getCierreSegunEstado(codigoAeroplanta: string, estado: number): Observable<CierreTurno> {
    return this.http.get<CierreTurno>(this.cierreURL + 'Estado?codigoAeroplanta=' + codigoAeroplanta + '&estado=' + estado)
  }

  postCierreIniciacion(codigoAeroplanta: string, responsable: Responsable): Observable<CierreTurno> {
    return this.http.post<CierreTurno>(this.cierreURL + codigoAeroplanta + '/Iniciar', responsable);
  }

  postCierreCargarDatos(codigoAeroplanta: string, carga: CargaDatos, cierre: CierreTurno): Observable<Comparativas> {
    return this.http.post<Comparativas>(this.cierreURL + cierre.id + '/CargarDatos', carga);
  }

  finalizarTurno(cierreId: string): Observable<string> {
    return this.http.put<string>(this.cierreTurnoBase + '/CierresTurnosV1/' + cierreId + '/Finalizar', '');
  }

  finalizarTurnoPromise(cierreId: string): Promise<string> {
    return this.http.put<string>(this.cierreTurnoBase + '/CierresTurnosV1/' + cierreId + '/Finalizar', '').toPromise();
  }

  putAsignarFechaReporte(cierreId: string, fechadeReporte: string): Observable<any> {
    return this.http.put<any>(this.cierreURL + `AsignarFechaReporte?cierreId=${cierreId}&fechadeReporte=${fechadeReporte}`, null)
  }

  obtenerParteDiario(cierreTurnoId: string): Promise<ParteDiario> {
    return this.http.get<ParteDiario>(`${this.cierreURL}ParteDiario?cierreTurnoId=${cierreTurnoId}`).toPromise();
  }

  modificarParteDiario(parte: ParteDiario, finaliza: boolean): Observable<ParteDiario> {
    return this.http.put<ParteDiario>(`${this.cierreURL}ParteDiario?finalizaTurno=${finaliza}`, parte);
  }

  modificarParteDiarioPromise(parte: ParteDiario, finaliza: boolean): Promise<ParteDiario> {
    return this.http.put<ParteDiario>(`${this.cierreURL}ParteDiario?finalizaTurno=${finaliza}`, parte).toPromise();
  }


}
